import moment from 'moment-timezone'

import type { Meeting, EventMembership } from 'features/meetings/types'
import { formatTimeRange } from 'services/time_format'

/** Returns true if the event has more than one host (collective, round-robin, or collective round-robin). */
export const isMultiHost = (
  event: Pick<Meeting, 'isCollective' | 'isRoundRobin' | 'isCollectiveRoundRobin'>
) => event.isCollective || event.isRoundRobin || event.isCollectiveRoundRobin

/**
 * Returns the formatted time range string for a buffer period.
 *
 * For a start buffer, the range spans from `bufferLength` minutes before
 * `eventStartTime` up to `eventStartTime`. For an end buffer, it spans from
 * `eventEndTime` to `bufferLength` minutes after `eventEndTime`.
 *
 * @param bufferLength - Buffer duration in minutes (as a string)
 * @param timeNotation - User's preferred time notation (12h or 24h)
 * @param eventStartTime - Start time of the meeting
 * @param eventEndTime - End time of the meeting
 * @param isStart - Whether this is a before-buffer (`true`) or after-buffer (`false`)
 */
export const calculateBufferRange = ({ bufferLength, timeNotation, eventStartTime, eventEndTime, isStart }: {
  bufferLength: string
  timeNotation: string
  eventStartTime: moment.Moment
  eventEndTime: moment.Moment
  isStart: boolean
}) => {
  let bufferStartTime
  let bufferEndTime

  if (isStart) {
    bufferStartTime = eventStartTime.clone().subtract(bufferLength, 'minutes')
    bufferEndTime = eventStartTime
  } else {
    bufferStartTime = eventEndTime
    bufferEndTime = eventEndTime.clone().add(bufferLength, 'minutes')
  }

  return formatTimeRange(bufferStartTime, bufferEndTime, timeNotation)
}

/**
 * Groups event memberships by their buffer duration for a given buffer type.
 *
 * Returns a map of buffer length (in minutes, as a string key) to the array
 * of memberships that share that duration. Memberships without a buffer of
 * the requested type are excluded.
 *
 * @param memberships - The event memberships to group
 * @param type - Whether to group by before-buffer (`"start"`) or after-buffer (`"end"`)
 */
export const getBufferData = (memberships: EventMembership[], type: 'start' | 'end') => {
  const key = type === 'start' ? 'beforeBufferTime' : 'afterBufferTime'
  const hasBuffer = type === 'start' ? 'hasBeforeBuffer' : 'hasAfterBuffer'

  return memberships.reduce<Record<string, EventMembership[]>>((acc, membership) => {
    if (membership[hasBuffer]) {
      const timeKey = String(membership[key])
      acc[timeKey] = acc[timeKey] || []
      acc[timeKey].push(membership)
    }
    return acc
  }, {})
}
